import { RemoteTileServiceConfigurationDc } from "@evergis/api";
import React, { createContext, FC, useCallback, useContext, useMemo, useState } from "react";
import { useNotification } from "../../../../hooks/notifications/useNotification";
import { DEFAULT_META, useResourceMeta } from "../../../../hooks/useResourceMeta";
import { LayerInfo } from "../../../../interfaces/resourceInfo";
import {
  importArcGISLayers as importArcGISLayersLayersResource,
  importWmsLayers as importWmsLayersResource,
} from "../../../../resources/import";
import { isValidUrl } from "../../../../utils/isValidUrl";
import { getResourceName } from "../../../../utils/resourceName";
import { AddProxyServiceContextType, ProxyServiceType } from "./types";

const AddProxyServiceContext = createContext<AddProxyServiceContextType>({});

function wmsDataToTree(data) {
  return Array.isArray(data)
    ? data.map(({ layer, name, title }) => {
        return {
          id: name,
          text: name,
          description: title,
          type: null,
          icon: layer ? "folder" : null,
          chip: null,
          children: wmsDataToTree(layer),
        };
      })
    : [];
}

function arcGISDataToTree(data) {
  return Array.isArray(data)
    ? data.map(({ layer, name, title }) => {
        return {
          id: name,
          text: `${title} (${name})`,
          type: null,
          icon: layer ? "folder" : null,
          chip: null,
          children: arcGISDataToTree(layer),
        };
      })
    : [];
}

interface AddProxyServiceProviderProps {
  layer?: LayerInfo;
}

export const SUBDOMAINS_SEPARATOR = ",";

export const AddProxyServiceProvider: FC<AddProxyServiceProviderProps> = ({ layer, children }) => {
  const [step, setStep] = useState(0);
  const [serviceType, setServiceType] = useState(
    (layer?.sourceType
      ? (layer?.sourceType as unknown as ProxyServiceType)
      : layer?.type === "RemoteTileService"
      ? ProxyServiceType.TMS
      : ProxyServiceType.WMS) || ProxyServiceType.WMS,
  );
  const [sourceUrl, setSourceUrl] = useState(
    (layer?.configuration as RemoteTileServiceConfigurationDc)?.urlFormat || "",
  );
  const [layers, setLayers] = useState<string[]>(layer?.proxy?.layers || []);
  const [subDomainsValue, setSubDomainsValue] = useState<string>(
    ((layer?.configuration as RemoteTileServiceConfigurationDc)?.subDomains || []).join(`${SUBDOMAINS_SEPARATOR} `) ||
      "",
  );
  const [meta, updateMeta] = useResourceMeta(
    layer
      ? {
          alias: layer.alias,
          description: layer.description,
          preview: {
            src: layer.preview,
          },
          copyrightText: layer.copyrightText,
          name: layer.name,
        }
      : { ...DEFAULT_META, name: getResourceName() },
  );
  const [importWmsLayers, { data: wmsLayersData, loading: wmsLayersLoading, error: wmsLayersError }] =
    importWmsLayersResource.use();
  const [importArcGISLayers, { data: arcGISLayersData, loading: arcGISLayersLoading, error: arcGISLayersError }] =
    importArcGISLayersLayersResource.use();

  const treeData = useMemo(() => {
    if (serviceType === ProxyServiceType.WMS) {
      return !wmsLayersLoading ? wmsDataToTree(wmsLayersData) : [];
    }

    return !arcGISLayersLoading ? arcGISDataToTree(arcGISLayersData) : [];
  }, [serviceType, wmsLayersData, wmsLayersLoading, arcGISLayersData, arcGISLayersLoading]);

  const importLayers = useCallback(() => {
    if (sourceUrl !== layer?.proxy?.sourceUrl) {
      setLayers([]);
    }

    if (isValidUrl(sourceUrl)) {
      if (serviceType === ProxyServiceType.WMS) {
        return importWmsLayers({ input: { url: sourceUrl } });
      }

      if (serviceType === ProxyServiceType.ArcGIS) {
        return importArcGISLayers({ input: { url: sourceUrl } });
      }
    } else {
      setStep(step - 1);
    }
  }, [serviceType, sourceUrl, importWmsLayers, importArcGISLayers, step, setStep]);

  useNotification(wmsLayersError?.message, {
    success: false,
    title: wmsLayersError?.message,
  });

  useNotification(arcGISLayersError?.message, {
    success: false,
    title: arcGISLayersError?.message,
  });

  const value: AddProxyServiceContextType = {
    step,
    setStep,
    serviceType,
    setServiceType,
    sourceUrl,
    setSourceUrl,
    meta,
    updateMeta,
    layers,
    setLayers,
    layersData: serviceType === ProxyServiceType.WMS ? wmsLayersData : arcGISLayersData,
    layersLoading: serviceType === ProxyServiceType.WMS ? wmsLayersLoading : arcGISLayersLoading,
    layersError: serviceType === ProxyServiceType.WMS ? wmsLayersError : arcGISLayersError,
    treeData,
    importLayers,
    subDomainsValue,
    setSubDomainsValue,
  };

  return <AddProxyServiceContext.Provider value={value}>{children}</AddProxyServiceContext.Provider>;
};

export const useAddProxyServiceContext = (): AddProxyServiceContextType => {
  return useContext(AddProxyServiceContext);
};
